import{isScheduledGiftAction,actionResourceVersionMatches,parsePreparedScheduledGift,isScheduledGiftWriteReceipt,isScheduledGiftStatusReceipt}from'./scheduled-gift-preparation';
import{parseScheduledGiftProjection,parseScheduledGiftReferences,type ScheduledGiftReference,type ScheduledGiftProjection}from'./scheduled-gift';
export{parseScheduledGiftProjection}from'./scheduled-gift';
import{needsCollectionMetadataPreparation,parsePreparedCollectionMetadata,collectionReviewDetails}from'./collection-metadata-preparation';
import{parseGrowthNavigation,type GrowthNavigation}from'./growth-navigation';
import{growthEntryOperations,parsePreparedGrowth}from'./growth-preparation';
import{isWorkspaceBulkAction,parsePreparedWorkspaceBulk}from'./workspace-bulk-preparation';
import{needsGiftPreparation,parsePreparedGift}from'./gift-preparation';
import{isSellerSetupReceipt}from"./seller-setup-completion";
import{normalizeDiscoveryInput,parsePreparedDiscovery}from'./catalog-discovery-preparation';
import{needsDigitalDeliveryPreparation,parsePreparedDigitalDelivery}from'./digital-delivery-preparation';
import {needsSellerDigitalPreparation,parsePreparedSellerDigital} from './seller-digital-preparation';
import {needsCatalogUploadPreparation,parsePreparedCatalogUpload} from './catalog-upload-preparation';
import {isShopSectionsAction,parsePreparedShopSections} from './shop-sections-preparation';
import {needsSavedItemsPreparation,parsePreparedSavedItems} from './saved-items-preparation';
import {needsPrivateCollectionPreparation,parsePreparedPrivateCollection} from './private-collections-preparation';
import{ordinaryCommerce,parsePreparedCommerce}from'./commerce-preparation';
import{ordinaryCustom,parsePreparedCustom}from'./custom-preparation';
import {ordinaryFulfillment,parsePreparedFulfillment} from './fulfillment-preparation';
import {nonSensitiveConversation,parsePreparedConversation} from './conversation-preparation';
import {validateDiscovery,type DiscoveryProjection} from "./discovery";
import { catalogDraft, catalogIssues } from "./catalog-draft";
/** Browser-only transport. No server modules or authority fields. The bearer
 * is kept in this tab's sessionStorage only (survives reload, not other tabs or
 * browser restarts); the server still decides expiry and revocation. */
const SESSION_STORAGE_PREFIX = "fablelot.session:";
function tabStorage(): Storage | undefined {
  try { return globalThis.sessionStorage; } catch { return undefined; }
}
import type { Command, Outcome } from "../../contracts/src/envelopes";
export type Money = { currency: "USD" | "EUR" | "JPY"; minor: string };
export type Field = {
  name: string;
  path?: (string | number)[];
  uploadPurpose?:
    | "personalization"
    | "conversation"
    | "listing"
    | "evidence"
    | "import"
    | "digital";
  accept?: string;
  maxBytes?: number;
  label: string;
  type:
    | "text"
    | "textarea"
    | "email"
    | "number"
    | "select"
    | "checkbox"
    | "file";
  required?: boolean;
  mustAccept?: boolean;
  options?: string[];
  value?: string | boolean;
  min?: number;
  max?: number;
};
export type Action = {
  id: Operation;
  instanceId: string;
  resource?: { id: string; version: number };
  immutableconfirmation?: Action["confirmation"];
  expectedVersion: number | null;
  label: string;
  subject: string;
  input: Record<string, unknown>;
  fields?: Field[];
  protected?: boolean;
  expiresAt?: string;
  confirmation?: {
    resource: { id: string; version: number };
    amount?: Money;
    before?: string;
    after?: string;
    principal: string;
    digest?: string;
    preparedByAgent?: boolean;
  };
  disabledReason?: string;
};
export type UploadReceipt = {
  id: string;
  version: number;
  state: "ready" | "quarantined" | "rejected";
  value?: Record<string, unknown>;
};
export type Card = {
  id: string;
  title: string;
  description?: string;
  image?: { url: string; alt: string };
  money?: Money;
  href?: string;
  kind?: string;
  sponsored?: boolean;
  original?: string;
  translation?: string;
  attribution?: string;
  demo?: boolean;
  facts?: { label: string; value: string }[];
};
export type View = {
  id: string;
  title?: string;
  version: number;
  updatedAt?: string;
  expiresAt?: string;
  items: Card[];
  actions: Action[];
  facts?: { label: string; value: string }[];
  messages?: {
    id: string;
    text: string;
    original?: string;
    attribution?: string;
    sentAt: string;
  }[];
  timeline?: { id: string; label: string; at: string; state: string }[];
  changes?: { field: string; before: string; after: string }[];
  totals?: { label: string; money: Money }[];
  downloads?: {
    id: string;
    label: string;
    expiresAt: string;
    kind?: "digital" | "attachment";
  }[];
  receipt?: { id: string; version: number; committedAt: string; outcome: "ok" };
  next?: string;
  reason?: string;
  data?: {
    scheduledGifts?:ScheduledGiftReference[];
    scheduledGiftStatus?:'setup-required'|'unavailable';
    growthEntry?:GrowthNavigation;
    discovery?: DiscoveryProjection;
    importPreview?: {
      rows: {
        row: number;
        accepted: boolean;
        reasonCodes: string[];
        listingId?: string;
        listingVersion?: number;
      }[];
    };
    continuingBilling?: {
      id: string;
      label: string;
      cadence: string;
      amount: Money;
      cancelHref: string;
    }[];
  };
};
export type State =
  | "loading"
  | "empty"
  | "ready"
  | "error"
  | "denied"
  | "pending"
  | "stale"
  | "success";
export class ApiError extends Error {
  constructor(
    public state: State,
    public code: string,
    public referenceId = "",
    public retryable = false,
    public httpStatus = 0,
  ) {
    super(/^[A-Z][A-Z0-9_]{0,95}$/.test(code) ? code : "REQUEST_UNAVAILABLE");
    this.code = this.message;
    this.referenceId = /^[A-Za-z0-9:_-]{1,200}$/.test(referenceId)
      ? referenceId
      : "";
  }
}
// Concrete boundary registry matches existing route factories; path mounting is integration-owned.
// Heterogeneous envelopes are intentional: checkout uses action+command fields,
// custom uses {action,command}; conversation selects operation in the path.
export const OPERATIONS = {
  "gifting.schedule":{path:"/api/thistleglass/gifting/scheduled/record",shape:"command"},
  "gifting.schedule.consent":{path:"/api/thistleglass/gifting/scheduled/consent",shape:"command"},
  "gifting.schedule.media":{path:"/api/thistleglass/gifting/scheduled/media/upload",shape:"command"},
  "gifting.collection.adopt":{path:"/api/thistleglass/gifting/collections/meta",action:"gifting.collection.adopt",shape:"flat"},
  "gifting.collection.create":{path:"/api/thistleglass/gifting/collections/meta",action:"gifting.collection.create",shape:"flat"},
  "gifting.collection.update":{path:"/api/thistleglass/gifting/collections/meta",action:"gifting.collection.update",shape:"flat"},
  "gifting.collection.share":{path:"/api/thistleglass/gifting/collections/meta",action:"gifting.collection.share",shape:"flat"},
  "gifting.collection.unshare":{path:"/api/thistleglass/gifting/collections/meta",action:"gifting.collection.unshare",shape:"flat"},
  "gifting.collection.delete":{path:"/api/thistleglass/gifting/collections/meta",action:"gifting.collection.delete",shape:"flat"},

  "gifting.slip":{path:"/api/thistleglass/gifting/slip",shape:"command"},
  "gifting.teaser":{path:"/api/thistleglass/gifting/teaser",shape:"command"},
  "gifting.teaser.consent":{path:"/api/thistleglass/gifting/teaser/consent",shape:"command"},
  "identity.sellerSetup":{path:"/api/thistleglass/account/seller/setup",shape:"command"},
  "identity.buyerPreferences":{path:"/api/thistleglass/account/preferences",shape:"command"},
  "catalog.discovery.declare":{path:"/api/thistleglass/catalog/discovery",shape:"command"},
  "digital.manifest.map": {path:"/api/thistleglass/digital",action:"digital.manifest.map",shape:"flat"},
  "digital.proof.manage": {path:"/api/thistleglass/digital",action:"digital.proof.manage",shape:"flat"},
  "digital.grant.manage": {path:"/api/thistleglass/digital",action:"digital.grant.manage",shape:"flat"},
  "digital.listing.upload": {path:"/api/thistleglass/digital/listing",action:"digital.listing.upload",shape:"flat"},
  "digital.listing.scan": {path:"/api/thistleglass/digital/listing",action:"digital.listing.scan",shape:"flat"},
  "digital.listing.declare": {path:"/api/thistleglass/digital/listing",action:"digital.listing.declare",shape:"flat"},
  "identity.recover": {
    path: "/api/thistleglass/identity/recovery",
    shape: "command",
  },
  "identity.claim": {
    path: "/api/thistleglass/identity/claim",
    shape: "command",
  },
  "identity.stepUp": {
    path: "/api/thistleglass/identity/step-up",
    shape: "command",
  },
  "purchase.review": {
    path: "/api/thistleglass/reviews/purchase",
    shape: "command",
  },
  "workspace.importPreview": {
    path: "/api/thistleglass/workspace/import/preview",
    shape: "command",
  },
  "workspace.importCommit": {
    path: "/api/thistleglass/workspace/import/commit",
    shape: "command",
  },
  "workspace.importRollback": {
    path: "/api/thistleglass/workspace/import/rollback",
    shape: "command",
  },
  "workspace.export": {
    path: "/api/thistleglass/workspace/export",
    shape: "command",
  },
  "operations.policyPropose": {
    path: "/api/thistleglass/operations/policies/propose",
    shape: "command",
  },
  "operations.policyApprove": {
    path: "/api/thistleglass/operations/policies/approve",
    shape: "command",
  },
  "operations.freeze": {
    path: "/api/thistleglass/operations/freeze",
    shape: "command",
  },
  "operations.restore": {
    path: "/api/thistleglass/operations/restore",
    shape: "command",
  },

  "checkout.address.save": {path:"/api/thistleglass/checkout",action:"checkout.address.save",shape:"flat"},
  "checkout.context.save": {path:"/api/thistleglass/checkout",action:"checkout.context.save",shape:"flat"},
  "cart.save": {
    path: "/api/thistleglass/checkout/cart",
    action: "checkout.cart.save",
    shape: "flat",
  },
  "checkout.prepare": {
    path: "/api/thistleglass/checkout/prepare",
    action: "checkout.prepare",
    shape: "flat",
  },
  "checkout.cancel": {
    path: "/api/thistleglass/checkout",
    action: "checkout.cancel",
    shape: "flat",
  },
  "aftersales.allocate": {
    path: "/api/thistleglass/aftersales/allocate",
    shape: "command",
  },
  "checkout.freeze": {
    path: "/api/thistleglass/checkout/freeze",
    action: "checkout.freeze",
    shape: "flat",
  },
  "checkout.confirm": {
    path: "/api/thistleglass/checkout/freeze",
    action: "checkout.confirm",
    shape: "flat",
  },
  "checkout.fund": {
    path: "/api/thistleglass/checkout/fund",
    action: "checkout.fund",
    shape: "flat",
  },
  "catalog.draft.create": {
    path: "/api/thistleglass/catalog/drafts/create",
    shape: "command",
  },
  "catalog.draft": {
    path: "/api/thistleglass/catalog/draft",
    shape: "command",
  },
  "catalog.publish": {
    path: "/api/thistleglass/catalog/publish",
    shape: "command",
  },
  "catalog.shop": { path: "/api/thistleglass/catalog/shop", shape: "command" },
  "catalog.shop.profile": { path: "/api/thistleglass/catalog/shop/profile", shape: "command" },
  "catalog.shop.policy": {path:"/api/thistleglass/catalog/shop/policy",shape:"command"},
  "identity.enroll": { path: "/api/thistleglass/identity", shape: "command" },
  "identity.submit": {
    path: "/api/thistleglass/identity?action=submit",
    shape: "command",
  },
  "identity.preferences": {
    path: "/api/thistleglass/identity?action=preferences",
    shape: "command",
  },
  "custom.request": {path: "/api/thistleglass/custom", action: "custom.request", shape: "nested"},
  "custom.version": {
    path: "/api/thistleglass/custom",
    action: "custom.quote.version",
    shape: "nested",
  },
  "custom.accept": {
    path: "/api/thistleglass/custom",
    action: "custom.quote.accept",
    shape: "nested",
  },
  "custom.renew": {
    path: "/api/thistleglass/custom",
    action: "custom.quote.renew",
    shape: "nested",
  },
  "custom.preserve": {
    path: "/api/thistleglass/custom",
    action: "custom.preservation.record",
    shape: "nested",
  },
  "conversation.block": {
    path: "/api/thistleglass/conversation/blockMember",
    shape: "command",
  },
  "conversation.schedule": {
    path: "/api/thistleglass/conversation/scheduleReply",
    shape: "command",
  },
  "conversation.registerAttachment": {
    path: "/api/thistleglass/conversation/registerAttachment",
    shape: "command",
  },
  "conversation.send": {
    path: "/api/thistleglass/conversation/send",
    shape: "command",
  },
  "conversation.draft": {
    path: "/api/thistleglass/conversation/saveDraft",
    shape: "command",
  },
  "conversation.report": {
    path: "/api/thistleglass/conversation/report",
    shape: "command",
  },
  "conversation.preference": {
    path: "/api/thistleglass/conversation/setPreference",
    shape: "command",
  },
  "catalog.stock.set": {path:"/api/thistleglass/catalog/stock",shape:"command"},
  "catalog.shop.vacation": {path:"/api/thistleglass/catalog/shop/vacation",shape:"command"},
  "catalog.shop.renew": {path:"/api/thistleglass/catalog/shop/renew",shape:"command"},
  "gifting.registry.save": {path:"/api/thistleglass/gifting/registry",shape:"command"},
  "gifting.save": { path: "/api/thistleglass/gifting", action: "gifting.save", shape: "flat" },
  "gifting.remove": { path: "/api/thistleglass/gifting/saved/remove", action: "gifting.remove", shape: "flat" },
  "gifting.claim": {
    path: "/api/thistleglass/gifting/claim",
    shape: "command",
  },
  "privacy.consent": {
    path: "/api/thistleglass/privacy/consent",
    shape: "command",
  },
  "privacy.export": {
    path: "/api/thistleglass/privacy/export",
    shape: "command",
  },
  "privacy.delete": {
    path: "/api/thistleglass/privacy/delete",
    shape: "command",
  },
  "privacy.resume": {
    path: "/api/thistleglass/privacy/resume",
    shape: "command",
  },
  "delegation.revoke": {
    path: "/api/thistleglass/delegation/revoke",
    shape: "command",
  },
  "delegation.prepare": {
    path: "/api/thistleglass/delegation/prepare",
    shape: "command",
  },
  "delegation.confirm": {
    path: "/api/thistleglass/delegation/confirm",
    shape: "command",
  },
  "aftersales.eligibility": {
    path: "/api/thistleglass/aftersales/eligibility",
    shape: "command",
  },
  "aftersales.refund": {
    path: "/api/thistleglass/aftersales/refund",
    shape: "command",
  },
  "fulfillment.ship": {
    path: "/api/thistleglass/fulfillment",
    action: "fulfillment.shipment.dispatch",
    shape: "flat",
  },
  "fulfillment.reserve": {
    path: "/api/thistleglass/fulfillment",
    action: "fulfillment.production.reserve",
    shape: "flat",
  },
  "fulfillment.quote": {
    path: "/api/thistleglass/fulfillment",
    action: "fulfillment.route.quote",
    shape: "flat",
  },
  "fulfillment.label": {
    path: "/api/thistleglass/fulfillment",
    action: "fulfillment.label.purchase",
    shape: "flat",
  },
  "fulfillment.labelRefund": {
    path: "/api/thistleglass/fulfillment",
    action: "fulfillment.label.refund",
    shape: "flat",
  },
  "case.submit": { path: "/api/thistleglass/cases", shape: "command" },
  "case.review": { path: "/api/thistleglass/cases/review", shape: "command" },
  "case.refund": { path: "/api/thistleglass/cases/monetary", shape: "command" },
  "trust.review": { path: "/api/thistleglass/trust/rights", shape: "command" },
  "trust.restrict": {
    path: "/api/thistleglass/trust/restrictions",
    shape: "command",
  },
  "workspace.pause": {
    path: "/api/thistleglass/workspace/pause",
    action: "workspace.pause.new-sales",
    shape: "nested",
  },
  "workspace.queue": {
    path: "/api/thistleglass/workspace/queue",
    action: "workspace.queue.read",
    shape: "nested",
  },
  "workspace.analytics": {
    path: "/api/thistleglass/workspace/analytics",
    action: "workspace.analytics.read",
    shape: "nested",
  },

  "growth.owner.enroll":{path:"/api/thistleglass/growth/owner/enroll",action:"growth.owner.enroll",shape:"flat"},
  "growth.promotion.create":{path:"/api/thistleglass/growth/entry",action:"growth.promotion.create",shape:"flat"},
  "growth.promotion.state":{path:"/api/thistleglass/growth/entry",action:"growth.promotion.state",shape:"flat"},
  "growth.cart.consent":{path:"/api/thistleglass/growth/entry",action:"growth.cart.consent",shape:"flat"},
  "growth.cart.withdraw":{path:"/api/thistleglass/growth/entry",action:"growth.cart.withdraw",shape:"flat"},
  "growth.offer.apply":{path:"/api/thistleglass/growth/entry",action:"growth.offer.apply",shape:"flat"},
  "growth.placement.place":{path:"/api/thistleglass/growth/entry",action:"growth.placement.place",shape:"flat"},
  "growth.stack": {
    path: "/api/thistleglass/growth",
    action: "growth.promotions.stack",
    shape: "nested",
  },
  "growth.spend": {
    path: "/api/thistleglass/growth",
    action: "growth.placement.spend",
    shape: "nested",
  },
  "growth.subscription.entitlement": {path:"/api/thistleglass/growth/subscription",shape:"command"},
  "growth.campaign.activate": {path:"/api/thistleglass/growth/campaigns/activate",shape:"command"},
  "growth.campaign.update": {path:"/api/thistleglass/growth/campaigns/state",shape:"command"},
  "trust.appeal.submit": {path:"/api/thistleglass/trust/appeals",shape:"command"},
  "trust.appeal.review": {path:"/api/thistleglass/trust/appeals/review",shape:"command"},
  "operations.reconcile": {
    path: "/api/thistleglass/operations/reconcile",
    shape: "command",
  },
} as const;
export type Operation = keyof typeof OPERATIONS;
const protectedOps = new Set<Operation>([
  "catalog.shop.vacation", "catalog.shop.renew",
  "growth.subscription.entitlement", "growth.campaign.activate", "growth.campaign.update",
  "trust.appeal.submit", "trust.appeal.review",
  "operations.policyPropose",
  "operations.policyApprove",
  "operations.freeze",
  "operations.restore",
  "identity.recover",
  "identity.claim",
  "identity.stepUp",
  "checkout.confirm",
  "checkout.fund",
  "catalog.publish",
  "aftersales.refund",
  "aftersales.allocate",
  "checkout.cancel",
  "case.review",
  "custom.accept",
  "custom.renew",
  "fulfillment.labelRefund",
  "identity.preferences",
  "delegation.confirm",
  "privacy.delete",
  "privacy.export",
  "delegation.revoke",
  "case.refund",
  "fulfillment.label",
  "growth.spend",
  "workspace.pause",
  "workspace.importCommit",
  "workspace.importRollback",
]);
export const requiresReview = (a: Action) =>
  !!a.protected ||
  protectedOps.has(a.id) ||
  (a.id === "catalog.shop" &&
    ["vacation", "renew"].includes(String(a.input.operation)));
export function reviewResourceMatches(
  action: Action,
  view: Pick<View, "id" | "version">,
): boolean {
  const subject = action.resource;
  return (
    !!subject &&
    !!action.confirmation &&
    action.confirmation.resource.id === subject.id &&
    action.confirmation.resource.version === subject.version
  );
}
const authority = new Set([
  "actor",
  "actorContext",
  "roles",
  "role",
  "tenant",
  "tenantId",
  "clientKind",
  "authTime",
  "__proto__",
  "constructor",
  "prototype",
]);
function rejectAuthority(input: unknown): void {
  if (!input || typeof input !== "object") return;
  for (const [k, v] of Object.entries(input)) {
    if (authority.has(k))
      throw new ApiError("denied", "CALLER_AUTHORITY_FORBIDDEN");
    rejectAuthority(v);
  }
}
/** Preserve original row identities when an editable catalog array is reordered/removed. */
function currentFieldPath(
  action: Action,
  field: Field,
  draft: Record<string, unknown>,
): (string | number)[] | undefined {
  const parts = field.path || [field.name];
  if (
    action.id !== "catalog.draft" ||
    !draft.catalogContent ||
    parts[0] !== "content" ||
    !["variants", "media", "translations"].includes(String(parts[1])) ||
    typeof parts[2] !== "number"
  )
    return parts;
  const group = String(parts[1]);
  const original = (action.input.content as any)?.[group];
  const current = (draft.catalogContent as any)?.[group];
  if (!Array.isArray(original) || !Array.isArray(current)) return parts;
  const row = original[parts[2]];
  if (!row) return ["content", group, current.length, ...parts.slice(3)];
  const identity = group === "translations" ? "locale" : "id";
  const index = current.findIndex((item) => item?.[identity] === row[identity]);
  return index < 0 ? undefined : ["content", group, index, ...parts.slice(3)];
}
export function fieldAvailable(
  action: Action,
  field: Field,
  draft: Record<string, unknown>,
): boolean {
  return currentFieldPath(action, field, draft) !== undefined;
}
export function fieldValue(
  action: Action,
  field: Field,
  draft: Record<string, unknown>,
): unknown {
  if (Object.hasOwn(draft, field.name)) return draft[field.name];
  if (
    field.value !== undefined &&
    !(draft.catalogContent && field.path?.[0] === "content")
  )
    return field.value;
  let value: unknown =
    action.id === "catalog.draft" && draft.catalogContent
      ? { ...action.input, content: draft.catalogContent }
      : action.input;
  for (const key of currentFieldPath(action, field, draft) || [])
    value =
      value && typeof value === "object"
        ? (value as Record<string, unknown>)[String(key)]
        : undefined;
  return value ?? (field.type === "checkbox" ? false : "");
}
export function actionInput(
  action: Action,
  draft: Record<string, unknown>,
): Record<string, unknown> {
  const input = structuredClone(action.input);
  if (action.id === "catalog.draft" && draft.catalogContent !== undefined) {
    const content = catalogDraft(draft.catalogContent);
    if (!content || catalogIssues(content).length)
      throw new ApiError("error", "INVALID_CATALOG_CONTENT");
    input.content = content;
  }
  for (const field of action.fields || []) {
    const parts = currentFieldPath(action, field, draft);
    if (!parts) continue;
    if (
      !parts.length ||
      parts.some((k) =>
        typeof k === "number"
          ? !Number.isSafeInteger(k) || k < 0 || k > 1000
          : !/^[_a-zA-Z][_a-zA-Z0-9]*$/.test(k) || authority.has(k),
      )
    )
      throw new ApiError("denied", "INVALID_FIELD_PATH");
    let target: any = input;
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i];
      if (!target[key] || typeof target[key] !== "object")
        target[key] = typeof parts[i + 1] === "number" ? [] : {};
      target = target[key];
    }
    if (
      field.type === "number" &&
      /minor$/i.test(String(parts[parts.length - 1]))
    )
      throw new ApiError("error", "INVALID_MONEY_FIELD");
    const value = fieldValue(action, field, draft);
    if (!validateField(field, value))
      throw new ApiError("error", "INVALID_FIELD");
    if (field.type === "file") {
      if (value === "") continue;
      if (!value || typeof value !== "object" || (value as any).uploadPending)
        throw new ApiError("pending", "UPLOAD_NOT_READY");
    }
    if (
      field.type === "number" &&
      (!/^-?\d+$/.test(String(value)) || !Number.isSafeInteger(Number(value)))
    )
      throw new ApiError("error", "INVALID_NUMBER");
    target[parts[parts.length - 1]] =
      field.type === "number" ? Number(value) : value;
  }
  if((action.id==="checkout.address.save"||action.id==="gifting.registry.save")&&input.address&&typeof input.address==="object"&&!Array.isArray(input.address)){
    const address=input.address as Record<string,unknown>;
    for(const key of ["line2","region"])if(address[key]==="")delete address[key];
  }
  rejectAuthority(input);
  return input;
}
export function envelope(
  operation: Operation,
  command: Command<Record<string, unknown>>,
): unknown {
  if (!Object.hasOwn(OPERATIONS, operation))
    throw new ApiError("denied", "UNREGISTERED_BOUNDARY_ACTION");
  if (
    typeof command.idempotencyKey !== "string" ||
    !command.idempotencyKey ||
    (command.expectedVersion !== null && !isVersion(command.expectedVersion))
  )
    throw new ApiError("error", "INVALID_COMMAND");
  canonicalJSON(command.input);
  rejectAuthority(command.input);
  const binding = OPERATIONS[operation];
  return binding.shape === "nested"
    ? { action: "action" in binding ? binding.action : undefined, command }
    : binding.shape === "flat"
      ? { action: "action" in binding ? binding.action : undefined, ...command }
      : command;
}
export const isVersion = (v: unknown): v is number =>
  typeof v === "number" && Number.isSafeInteger(v) && v >= 1;
function requireShape(condition: unknown): asserts condition {
  if (!condition) throw new ApiError("error", "INVALID_READ_MODEL");
}
const text = (value: unknown) => typeof value === "string";
const optionalText = (value: unknown) => value === undefined || text(value);
const money = (value: any) =>
  !!value &&
  ["USD", "EUR", "JPY"].includes(value.currency) &&
  typeof value.minor === "string" &&
  /^(?:0|-?[1-9][0-9]*)$/.test(value.minor);
function facts(value: unknown) {
  requireShape(
    value === undefined ||
      (Array.isArray(value) &&
        value.every((f) => f && text(f.label) && text(f.value))),
  );
}
export function parseView(value: unknown): View {
  if (!value || typeof value !== "object")
    throw new ApiError("error", "INVALID_READ_MODEL");
  const v = value as View;
  if (
    typeof v.id !== "string" ||
    !isVersion(v.version) ||
    !Array.isArray(v.items) ||
    !Array.isArray(v.actions)
  )
    throw new ApiError("error", "INVALID_READ_MODEL");
  if (
    v.receipt &&
    (!isVersion(v.receipt.version) ||
      typeof v.receipt.id !== "string" ||
      !v.receipt.id.trim() ||
      v.receipt.outcome !== "ok" ||
      !Number.isFinite(Date.parse(v.receipt.committedAt)))
  )
    throw new ApiError("error", "INVALID_RECEIPT");
  for (const key of [
    "title",
    "updatedAt",
    "expiresAt",
    "next",
    "reason",
  ] as const)
    requireShape(optionalText(v[key]));
  if (v.expiresAt) requireShape(Number.isFinite(Date.parse(v.expiresAt)));
  if (v.data !== undefined) {
    requireShape(
      v.data && typeof v.data === "object" && !Array.isArray(v.data),
    );
    if(v.data.scheduledGifts!==undefined)parseScheduledGiftReferences(v.data.scheduledGifts);
    if(v.data.scheduledGiftStatus!==undefined)requireShape(['setup-required','unavailable'].includes(v.data.scheduledGiftStatus));
    if (v.data.growthEntry!==undefined) {try{parseGrowthNavigation(v.data.growthEntry)}catch{requireShape(false)}}
    if (v.data.importPreview) {
      requireShape(
        Array.isArray(v.data.importPreview.rows) &&
          v.data.importPreview.rows.length <= 10000,
      );
      for (const row of v.data.importPreview.rows)
        requireShape(
          row &&
            Number.isSafeInteger(row.row) &&
            row.row >= 1 &&
            typeof row.accepted === "boolean" &&
            Array.isArray(row.reasonCodes) &&
            row.reasonCodes.every(text) &&
            optionalText(row.listingId) &&
            (row.listingVersion === undefined || isVersion(row.listingVersion)),
        );
    }
    if (v.data.continuingBilling) {
      requireShape(Array.isArray(v.data.continuingBilling));
      for (const row of v.data.continuingBilling)
        requireShape(
          row &&
            text(row.id) &&
            text(row.label) &&
            text(row.cadence) &&
            money(row.amount) &&
            text(row.cancelHref),
        );
    }
  }
  facts(v.facts);
  for (const key of [
    "messages",
    "timeline",
    "changes",
    "totals",
    "downloads",
  ] as const)
    requireShape(v[key] === undefined || Array.isArray(v[key]));
  for (const m of v.messages || [])
    requireShape(
      m &&
        text(m.id) &&
        text(m.text) &&
        text(m.sentAt) &&
        optionalText(m.original) &&
        optionalText(m.attribution),
    );
  for (const t of v.timeline || [])
    requireShape(
      t && text(t.id) && text(t.label) && text(t.at) && text(t.state),
    );
  for (const change of v.changes || [])
    requireShape(
      change && text(change.field) && text(change.before) && text(change.after),
    );
  for (const total of v.totals || [])
    requireShape(total && text(total.label) && money(total.money));
  for (const d of v.downloads || [])
    requireShape(
      d &&
        text(d.id) &&
        text(d.label) &&
        text(d.expiresAt) &&
        Number.isFinite(Date.parse(d.expiresAt)) &&
        (d.kind === undefined ||
          d.kind === "digital" ||
          d.kind === "attachment"),
    );
  const instances = new Set<string>();
  for (const a of v.actions) {
    requireShape(
      !!a?.resource && text(a.resource.id) && isVersion(a.resource.version),
    );
    requireShape(
      actionResourceVersionMatches(a),
    );
    requireShape(
      typeof a.instanceId === "string" &&
        a.instanceId.length > 0 &&
        a.instanceId.length <= 200 &&
        !instances.has(a.instanceId),
    );
    instances.add(a.instanceId);
    requireShape(a && typeof a === "object");
    requireShape(optionalText(a.expiresAt) && optionalText(a.disabledReason));
    if (a.expiresAt) requireShape(Number.isFinite(Date.parse(a.expiresAt)));
    if (a.confirmation)
      requireShape(
        text(a.confirmation.principal) &&
          a.confirmation.resource &&
          text(a.confirmation.resource.id) &&
          isVersion(a.confirmation.resource.version) &&
          (!a.confirmation.amount || money(a.confirmation.amount)) &&
          optionalText(a.confirmation.before) &&
          optionalText(a.confirmation.after),
      );
    if (
      (a.expectedVersion !== null && !isVersion(a.expectedVersion)) ||
      !Object.hasOwn(OPERATIONS, a.id) ||
      typeof a.label !== "string" ||
      typeof a.subject !== "string" ||
      !a.input ||
      typeof a.input !== "object" ||
      Array.isArray(a.input)
    )
      throw new ApiError("error", "INVALID_ACTION");
    rejectAuthority(a.input);
    if (a.fields && !Array.isArray(a.fields))
      throw new ApiError("error", "INVALID_FIELDS");
    const names = new Set<string>();
    for (const f of a.fields || []) {
      requireShape(!names.has(f.name));
      names.add(f.name);
      requireShape(
        (f.required === undefined || typeof f.required === "boolean") &&
          (f.mustAccept === undefined ||
            (typeof f.mustAccept === "boolean" && f.type === "checkbox")),
      );
      requireShape(
        (f.min === undefined || Number.isSafeInteger(f.min)) &&
          (f.max === undefined || Number.isSafeInteger(f.max)) &&
          (f.min === undefined || f.max === undefined || f.min <= f.max),
      );
      requireShape(f && typeof f === "object");
      if (f.path)
        requireShape(
          Array.isArray(f.path) &&
            f.path.length > 0 &&
            f.path.length <= 12 &&
            f.path.every((k) =>
              typeof k === "number"
                ? Number.isSafeInteger(k) && k >= 0 && k <= 1000
                : typeof k === "string" &&
                  /^[_a-zA-Z][_a-zA-Z0-9]*$/.test(k) &&
                  !authority.has(k),
            ),
        );
      if (f.maxBytes !== undefined)
        requireShape(Number.isSafeInteger(f.maxBytes) && f.maxBytes > 0);
      if (f.type === "file")
        requireShape(
          [
            "personalization",
            "conversation",
            "listing",
            "evidence",
            "import",
            "digital",
          ].includes(f.uploadPurpose || ""),
        );
      if (
        !/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/.test(f.name) ||
        authority.has(f.name) ||
        ![
          "text",
          "textarea",
          "email",
          "number",
          "select",
          "checkbox",
          "file",
        ].includes(f.type) ||
        typeof f.label !== "string" ||
        (f.options &&
          (!Array.isArray(f.options) || f.options.some((o) => !text(o))))
      )
        throw new ApiError("error", "INVALID_FIELD");
    }
  }
  for (const c of v.items) {
    requireShape(c && text(c.id) && text(c.title));
    for (const key of [
      "description",
      "href",
      "kind",
      "original",
      "translation",
      "attribution",
    ] as const)
      requireShape(optionalText(c[key]));
    if (c.image) requireShape(text(c.image.url) && text(c.image.alt));
    if (c.money) requireShape(money(c.money));
    facts(c.facts);
  }
  if (v.data?.discovery !== undefined) {
    try { validateDiscovery(v.data.discovery, v.items); }
    catch { throw new ApiError("error", "INVALID_DISCOVERY_PROJECTION"); }
  }
  return v;
}
export function safeHref(
  href: string,
  kind: "internal" | "image" = "internal",
): string | undefined {
  try {
    const u = new URL(href, "https://client.invalid");
    if (kind === "internal")
      return u.origin === "https://client.invalid" && !href.startsWith("//")
        ? u.pathname + u.search + u.hash
        : undefined;
    return ["https:"].includes(u.protocol) ||
      u.origin === "https://client.invalid"
      ? href
      : undefined;
  } catch {
    return undefined;
  }
}
/** Public image references from the exact configured API may use private-preview HTTP. */
export function imageHref(href: string, apiOrigin: string): string | undefined {
  try {
    const url = new URL(href, apiOrigin);
    if (url.username || url.password || href.startsWith("//")) return;
    if (
      (href.startsWith("/api/thistleglass/") ||
        (/^https?:\/\//i.test(href) &&
          url.origin === new URL(apiOrigin).origin)) &&
      ["http:", "https:"].includes(url.protocol)
    )
      return url.href;
    return safeHref(href, "image");
  } catch {
    return;
  }
}
type ScheduledMediaFile={action:Action;principal:string;instanceId:string;detach?:()=>void;sourceInstance:string;file:File;epoch:number;fingerprint:string;controller:AbortController;used:boolean;expiry:ReturnType<typeof setTimeout>};
const nativeTransportInstances=new WeakSet<object>();
export function isNativeTransport(value:unknown):value is Transport{return !!value&&typeof value==='object'&&nativeTransportInstances.has(value)}
export type OwnedScheduledGiftMedia=Readonly<{id:string;version:1;kind:'audio'|'video';mime:'audio/ogg'|'audio/mpeg'|'video/mp4'|'video/webm';bytes:number}>;
export class Transport {
  async scheduledGiftOwnedMedia(action:Action,signal?:AbortSignal):Promise<readonly OwnedScheduledGiftMedia[]>{
    if(action.id!=='gifting.schedule.media'||!actionResourceVersionMatches(action)||action.protected!==true||!/^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(action.instanceId)||!action.expiresAt||!Number.isFinite(Date.parse(action.expiresAt)))throw new ApiError('denied','GIFT_MEDIA_TEMPLATE_REQUIRED');
    const epoch=this.epoch,fingerprint=this.scheduledMediaFingerprint(action),current=()=>{if(epoch!==this.epoch)throw new ApiError('denied','SESSION_CHANGED');if(signal?.aborted||fingerprint!==this.scheduledMediaFingerprint(action)||Date.parse(action.expiresAt!)<=Date.now())throw new ApiError('stale','SOURCE_VIEW_CHANGED')};current();
    const raw:any=await this.request('/api/thistleglass/gifting/scheduled/media/owned?instanceId='+encodeURIComponent(action.instanceId),{method:'GET',signal,redirect:'error'});current();
    const exact=(value:any,keys:string[])=>!!value&&typeof value==='object'&&!Array.isArray(value)&&Object.keys(value).length===keys.length&&keys.every(k=>Object.hasOwn(value,k));
    if(!exact(raw,['kind','version','value'])||raw.kind!=='ok'||raw.version!==1||!exact(raw.value,['items'])||!Array.isArray(raw.value.items)||raw.value.items.length>100)throw new ApiError('pending','GIFT_MEDIA_METADATA_UNRESOLVED');
    const seen=new Set<string>(),items:OwnedScheduledGiftMedia[]=[];
    for(const v of raw.value.items){if(!exact(v,['id','version','kind','mime','bytes'])||typeof v.id!=='string'||!/^[a-f0-9]{64}$/.test(v.id)||seen.has(v.id)||v.version!==1||!['audio/ogg','audio/mpeg','video/mp4','video/webm'].includes(v.mime)||v.kind!==(v.mime.startsWith('audio/')?'audio':'video')||!Number.isSafeInteger(v.bytes)||v.bytes<1||v.bytes>10*1024*1024)throw new ApiError('pending','GIFT_MEDIA_METADATA_UNRESOLVED');seen.add(v.id);items.push(Object.freeze({id:v.id,version:1,kind:v.kind,mime:v.mime,bytes:v.bytes}))}current();return Object.freeze(items);
  }
  private scheduledMediaFiles=new Map<string,ScheduledMediaFile>();
  private scheduledMediaOwners=new WeakMap<Action,ScheduledMediaFile>();
  private scheduledMediaFingerprint(action:Action){return canonicalJSON({id:action.id,instanceId:action.instanceId,subject:action.subject,resource:action.resource,expectedVersion:action.expectedVersion,input:action.input,expiresAt:action.expiresAt,...((action.confirmation??action.immutableconfirmation)?{confirmation:action.confirmation??action.immutableconfirmation}:{})})}
  releaseScheduledGiftMedia(action:Action){const held=this.scheduledMediaOwners.get(action);if(!held)return;this.scheduledMediaOwners.delete(action);if(this.scheduledMediaFiles.get(held.instanceId)===held)this.scheduledMediaFiles.delete(held.instanceId);held.detach?.();clearTimeout(held.expiry);held.controller.abort()}
  async prepareScheduledGiftMedia(action:Action,file:File,signal?:AbortSignal):Promise<Action>{
    if(action.id!=='gifting.schedule.media'||!actionResourceVersionMatches(action)||action.protected!==true||typeof File==='undefined'||!(file instanceof File)||!['audio/ogg','audio/mpeg','video/mp4','video/webm'].includes(file.type)||!Number.isSafeInteger(file.size)||file.size<1||file.size>10*1024*1024)throw new ApiError('error','GIFT_MEDIA_FILE_REQUIRED');
    for(const held of this.scheduledMediaFiles.values())if(held.sourceInstance===action.instanceId)this.releaseScheduledGiftMedia(held.action);
    if(!action.expiresAt||!Number.isFinite(Date.parse(action.expiresAt))||Date.parse(action.expiresAt)<=Date.now()||signal?.aborted)throw new ApiError('stale','SOURCE_VIEW_CHANGED');const epoch=this.epoch,fingerprint=this.scheduledMediaFingerprint(action),assertCurrent=()=>{if(epoch!==this.epoch)throw new ApiError('denied','SESSION_CHANGED');if(signal?.aborted||fingerprint!==this.scheduledMediaFingerprint(action)||Date.parse(action.expiresAt!)<=Date.now())throw new ApiError('stale','SOURCE_VIEW_CHANGED')},bytes=await file.arrayBuffer();assertCurrent();if(bytes.byteLength!==file.size)throw new ApiError('error','GIFT_MEDIA_FILE_REQUIRED');const digest=await crypto.subtle.digest('SHA-256',bytes);assertCurrent();
    const sha256=Array.from(new Uint8Array(digest),b=>b.toString(16).padStart(2,'0')).join(''),input={orderId:action.input.orderId,lineId:action.input.lineId,registryId:action.input.registryId,itemId:action.input.itemId,mime:file.type,sha256,bytes:file.size},prepared=await this.prepare(action,input);assertCurrent();
    const controller=new AbortController(),expiry=setTimeout(()=>this.releaseScheduledGiftMedia(prepared),Math.max(0,Date.parse(prepared.expiresAt!)-Date.now()));(expiry as any).unref?.();const held:ScheduledMediaFile={action:prepared,principal:prepared.confirmation!.principal,instanceId:prepared.instanceId,sourceInstance:action.instanceId,file,epoch,fingerprint:this.scheduledMediaFingerprint(prepared),controller,used:false,expiry};this.scheduledMediaFiles.set(held.instanceId,held);this.scheduledMediaOwners.set(prepared,held);if(signal){const abort=()=>this.releaseScheduledGiftMedia(prepared);signal.addEventListener('abort',abort,{once:true});held.detach=()=>signal.removeEventListener('abort',abort)}return prepared;
  }

  private token = "";
  private epoch = 0;
  private revocationListeners = new Set<() => void>();
  onSessionRevoked(listener: () => void) {
    this.revocationListeners.add(listener);
    return () => {
      this.revocationListeners.delete(listener);
    };
  }
  private revokeCurrentSession(epoch: number) {
    if (epoch !== this.epoch || !this.token) return;
    this.setSession("");
    for (const listener of this.revocationListeners) listener();
  }
  get sessionEpoch() {
    return this.epoch;
  }
  get authenticated() {
    return !!this.token;
  }
  constructor(
    readonly origin: string,
    private fetcher: typeof fetch = (input, init) =>
      globalThis.fetch(input, init),
  ) {
    const u = new URL(origin);
    if (
      !["http:", "https:"].includes(u.protocol) ||
      u.username ||
      u.password ||
      u.search ||
      u.hash
    )
      throw new Error("Invalid API origin");
    nativeTransportInstances.add(this);
    try {
      const saved = tabStorage()?.getItem(SESSION_STORAGE_PREFIX + u.origin);
      if (saved && /^[A-Za-z0-9._~+/=-]{16,512}$/.test(saved)) this.token = saved;
    } catch {}
  }
  setSession(token: string) {
    for(const held of this.scheduledMediaFiles.values())this.releaseScheduledGiftMedia(held.action);this.scheduledMediaFiles.clear();
    this.token = token;
    this.epoch++;
    try {
      const storage = tabStorage(), key = SESSION_STORAGE_PREFIX + new URL(this.origin).origin;
      if (token) storage?.setItem(key, token); else storage?.removeItem(key);
    } catch {}
  }
  private async request(
    path: string,
    init: RequestInit = {},
  ): Promise<unknown> {
    const epoch = this.epoch;
    let response: Response;
    try {
      response = await this.fetcher(new URL(path, this.origin), {
        ...init,
        credentials: "omit",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          ...(init.body && !(init.body instanceof FormData)
            ? { "Content-Type": "application/json" }
            : {}),
          ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
          ...init.headers,
        },
      });
    } catch (e) {
      if ((e as Error).name === "AbortError") throw e;
      throw new ApiError("error", "NETWORK_UNAVAILABLE", "", true);
    }
    if (epoch !== this.epoch) throw new ApiError("denied", "SESSION_CHANGED");
    if (response.status === 401) this.revokeCurrentSession(epoch);
    const responseEpoch = this.epoch;
    let result: any;
    try {
      result = await response.json();
    } catch {
      throw new ApiError("error", "INVALID_RESPONSE", "", false);
    }
    if (responseEpoch !== this.epoch)
      throw new ApiError("denied", "SESSION_CHANGED");
    if (!result || typeof result !== "object" || Array.isArray(result))
      throw new ApiError("error", "INVALID_RESPONSE");

    if (
      result.kind === "denied" ||
      result.kind === "conflict" ||
      result.kind === "pending"
    ) {
      if (
        typeof result.code !== "string" ||
        typeof result.referenceId !== "string" ||
        typeof result.retryable !== "boolean"
      )
        throw new ApiError("error", "INVALID_RESPONSE");
      throw new ApiError(
        result.kind === "conflict" ? "stale" : result.kind,
        result.code,
        result.referenceId,
        result.retryable === true,
      );
    }
    if (!response.ok) {
      const failure = new ApiError(
        response.status === 401 || response.status === 403
          ? "denied"
          : response.status === 409
            ? "stale"
            : response.status === 503
              ? "pending"
              : "error",
        result.code || `HTTP_${response.status}`,
        result.referenceId || "",
        false,
        response.status,
      );
      // Plain-language explanation from the server, when it sends one.
      if (typeof result.message === "string") (failure as ApiError & { detail?: string }).detail = result.message.slice(0, 300);
      throw failure;
    }
    return result;
  }
  async read(
    screen: string,
    path: string,
    locale: string,
    query: string,
    signal?: AbortSignal,
  ): Promise<View> {
    const params = new URLSearchParams();
    for (const [key, value] of new URLSearchParams(query))
      if (["q", "destination", "kind", "sort", "currency", "cursor", "after"].includes(key) || (["B01","B02"].includes(screen)&&["category","priceMin","priceMax","origin","readiness","personalization","transitMax","shop"].includes(key)) || (screen==='S10'&&['growthListing','growthCampaign','growthCart','growthAfter','growthAfterSource','growthAfterCampaign','growthAfterDefinition'].includes(key)) || (screen==='B07'&&['growthCart','growthAfter','growthOffer','growthShop','growthAfterShop','growthAfterOffer'].includes(key)))
        params.append(key, value);
    params.set("screen", screen);
    // ar/he are display-only RTL stress routes; the source API keeps its
    // existing English fallback and unchanged subject tail/authorization.
    const apiPath = locale === "en" && /^\/(?:ar|he)(?:\/|$)/.test(path)
      ? path.replace(/^\/(?:ar|he)(?=\/|$)/, "/en") : path;
    params.set("path", apiPath);
    params.set("locale", locale);
    const r: any = await this.request("/api/thistleglass/views?" + params, {
      signal,
    });
    return parseView(r.kind === "ok" ? r.value : r);
  }
  /** Closed guest mailbox association preparation. These two routes issue no
   * enrollment, payment, claim association or client-chosen authority. */
  async beginGuestOrderClaim(command:{idempotencyKey:string;expectedVersion:null;input:{orderId:string;email:string;nonce:string;locale:string}}):Promise<{challengeId:string;version:1;expiresAt:string}>{
    if(!/^[a-f0-9]{64}$/.test(command.idempotencyKey)||command.expectedVersion!==null||!/^[a-f0-9]{64}$/.test(command.input.nonce)||!['en','es','fr','de','ja'].includes(command.input.locale))throw new ApiError('error','INVALID_GUEST_CLAIM_INPUT');canonicalJSON(command);
    const epoch=this.epoch,r:any=await this.request('/api/thistleglass/account/guest/order/claim/begin',{method:'POST',body:JSON.stringify(command)});if(epoch!==this.epoch)throw new ApiError('denied','SESSION_CHANGED');if(!r||typeof r.challengeId!=='string'||!/^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(r.challengeId)||r.version!==1||r.state!=='pending'||typeof r.expiresAt!=='string'||!Number.isFinite(Date.parse(r.expiresAt))||Date.parse(r.expiresAt)<=Date.now())throw new ApiError('pending','GUEST_CLAIM_PREPARATION_UNRESOLVED');return{challengeId:r.challengeId,version:1,expiresAt:r.expiresAt};
  }
  async verifyGuestOrderClaim(command:{idempotencyKey:string;expectedVersion:1;input:{challengeId:string;nonce:string;proof:string}},orderId:string):Promise<Action>{
    if(!/^[a-f0-9]{64}$/.test(command.idempotencyKey)||command.expectedVersion!==1||!/^[a-f0-9]{64}$/.test(command.input.nonce)||!/^(\d{6}|[a-f0-9]{64})$/.test(command.input.proof.replace(/[\s-]/g,"")))throw new ApiError('error','INVALID_GUEST_CLAIM_INPUT');canonicalJSON(command);
    const epoch=this.epoch,r:any=await this.request('/api/thistleglass/account/guest/order/claim/verify',{method:'POST',body:JSON.stringify(command)});if(epoch!==this.epoch)throw new ApiError('denied','SESSION_CHANGED');if(r?.kind!=='ok'||r.version!==1||!r.value?.action)throw new ApiError('pending','GUEST_CLAIM_PREPARATION_UNRESOLVED');const action=parseView({id:'A01',version:1,items:[],actions:[r.value.action]}).actions[0];if(action.id!=='identity.claim'||action.subject!==orderId||action.resource?.version!==1||action.expectedVersion!==1||action.protected!==true||action.fields?.length!==0||action.input.kind!=='guestClaim'||typeof action.input.ownershipProof!=='string'||!/^[a-f0-9]{64}$/.test(action.input.ownershipProof)||!action.expiresAt||Date.parse(action.expiresAt)<=Date.now())throw new ApiError('pending','GUEST_CLAIM_PREPARATION_UNRESOLVED');return action;
  }
  /** Closed no-effect proposal port: never constructs a Command/native key. */
  async prepareCaseProposal(action: Action, input: Record<string, unknown>): Promise<{proposalId: string; caseId: string; caseVersion: number; materialDigest: string; expiresAt: string; requiresIndependentReview: true}> {
    if (action.id !== "aftersales.allocate" || !action.resource || action.expectedVersion !== action.resource.version || action.input.caseId !== action.resource.id || input.caseId !== action.resource.id || !action.expiresAt || !Number.isFinite(Date.parse(action.expiresAt)) || Date.parse(action.expiresAt) <= Date.now())
      throw new ApiError("stale", "CURRENT_CASE_PROPOSAL_REQUIRED");
    canonicalJSON(input);
    const epoch = this.epoch;
    const r: any = await this.request("/api/thistleglass/cases/refund-proposal/prepare", {method: "POST", body: JSON.stringify({instanceId: action.instanceId, expectedVersion: action.expectedVersion, input})});
    if (epoch !== this.epoch) throw new ApiError("denied", "SESSION_CHANGED");
    const v = r?.value;
    if (r?.kind !== "ok" || !isVersion(r.version) || !v || typeof v.proposalId !== "string" || !/^[A-Za-z0-9_-]{1,200}$/.test(v.proposalId) || v.caseId !== action.resource.id || v.caseVersion !== action.expectedVersion || !/^[a-f0-9]{64}$/.test(v.materialDigest) || typeof v.expiresAt !== "string" || !Number.isFinite(Date.parse(v.expiresAt)) || Date.parse(v.expiresAt) <= Date.now() || v.requiresIndependentReview !== true)
      throw new ApiError("pending", "CASE_PROPOSAL_UNRESOLVED");
    return {proposalId: v.proposalId,caseId: v.caseId,caseVersion: v.caseVersion,materialDigest: v.materialDigest,expiresAt: v.expiresAt,requiresIndependentReview: true};
  }
  /** Closed declaration-only port; no native key, proof, publish or payment. */
  async declarePublication(action: Action, input: {creative: Record<string,unknown>;price:{currency:string;minor:string}}): Promise<void> {
    const ref=action.input.declarationFor as {id?:unknown;version?:unknown}|undefined;
    if(action.id!=="catalog.publish"||!action.resource||action.expectedVersion!==action.resource.version||!ref||ref.id!==action.resource.id||ref.version!==action.expectedVersion||!action.expiresAt||Date.parse(action.expiresAt)<=Date.now())throw new ApiError("stale","CURRENT_PUBLICATION_DECLARATION_REQUIRED");
    canonicalJSON(input);const epoch=this.epoch;
    const result:any=await this.request("/api/thistleglass/catalog/publication-declaration",{method:"POST",body:JSON.stringify({instanceId:action.instanceId,expectedVersion:action.expectedVersion,input})});
    if(epoch!==this.epoch)throw new ApiError("denied","SESSION_CHANGED");
    if(result?.kind!=="ok"||!isVersion(result.version)||result.value?.declarationRecorded!==true||result.value?.draft?.id!==action.resource.id||result.value?.draft?.version!==action.expectedVersion||result.value?.requiresImmutableReview!==true)throw new ApiError("pending","PUBLICATION_DECLARATION_UNRESOLVED");
  }
  async prepare(
    action: Action,
    input: Record<string, unknown>,
  ): Promise<Action> {
    input=normalizeDiscoveryInput(action,input);
    if (
      !actionResourceVersionMatches(action)
    )
      throw new ApiError("stale", "ACTION_RESOURCE_REQUIRED");
    canonicalJSON(input);
    const epoch = this.epoch;
    if(action.id==='gifting.schedule.consent'){const prepared=await parsePreparedScheduledGift(action,input,{kind:'ok',version:action.resource!.version,value:{preparedAction:action}});if(epoch!==this.epoch)throw new ApiError('denied','SESSION_CHANGED');return prepared}
    const r: any = await this.request("/api/thistleglass/actions/prepare", {
      method: "POST",
      body: JSON.stringify({
        instanceId: action.instanceId,
        expectedVersion: action.expectedVersion,
        input,
      }),
    });
    if (epoch !== this.epoch) throw new ApiError("denied", "SESSION_CHANGED");
    if(isScheduledGiftAction(action)){const prepared=await parsePreparedScheduledGift(action,input,r);if(epoch!==this.epoch)throw new ApiError('denied','SESSION_CHANGED');return prepared}
    if(isWorkspaceBulkAction(action)){const prepared=await parsePreparedWorkspaceBulk(action,input,r);if(epoch!==this.epoch)throw new ApiError('denied','SESSION_CHANGED');return prepared;}
    if(needsGiftPreparation(action)){const prepared=await parsePreparedGift(action,input,r);if(epoch!==this.epoch)throw new ApiError('denied','SESSION_CHANGED');return prepared;}
    if(needsDigitalDeliveryPreparation(action))return parsePreparedDigitalDelivery(action,input,r);
    if(needsSellerDigitalPreparation(action))return parsePreparedSellerDigital(action,input,r);
    if(needsCatalogUploadPreparation(action))return parsePreparedCatalogUpload(action,input,r);
    if(isShopSectionsAction(action))return parsePreparedShopSections(action,input,r);
    if(needsCollectionMetadataPreparation(action))return parsePreparedCollectionMetadata(action,input,r);
    if(needsPrivateCollectionPreparation(action))return parsePreparedPrivateCollection(action,input,r);
    if(needsSavedItemsPreparation(action))return parsePreparedSavedItems(action,input,r);
    if(ordinaryCommerce.has(action.id)&&!requiresReview(action))return parsePreparedCommerce(action,input,r);
    if(ordinaryCustom.has(action.id)&&!requiresReview(action))return parsePreparedCustom(action,input,r);
    if(nonSensitiveConversation.has(action.id)&&!requiresReview(action))return parsePreparedConversation(action,input,r);
    if(ordinaryFulfillment.has(action.id)&&!requiresReview(action))return parsePreparedFulfillment(action,input,r);
    if(action.id==='catalog.discovery.declare')return parsePreparedDiscovery(action,input,r);
    if(growthEntryOperations.has(action.id))return parsePreparedGrowth(action,input,r);
    const raw = r?.value?.preparedAction;
    if (r?.kind !== "ok" || !isVersion(r.version) || !raw)
      throw new ApiError("pending", "PREPARATION_UNRESOLVED");
    const prepared: Action = {
      ...raw,
      confirmation: raw.immutableconfirmation,
    };
    parseView({
      id: "preparation",
      version: r.version,
      items: [],
      actions: [prepared],
    });
    canonicalJSON(prepared.input);
    const target =
      action.id === "delegation.prepare" ? "delegation.confirm" : action.id;
    const confirmation = prepared.confirmation;
    if (
      prepared.id !== target ||
      prepared.instanceId === action.instanceId ||
      prepared.resource?.id !== action.resource.id ||
      prepared.resource?.version !== action.resource.version ||
      prepared.expectedVersion !== action.expectedVersion ||
      !Array.isArray(prepared.fields) ||
      prepared.fields.length !== 0 ||
      !prepared.expiresAt ||
      Date.parse(prepared.expiresAt) <= Date.now() ||
      !confirmation ||
      !reviewResourceMatches(prepared, prepared.resource!) ||
      !confirmation.principal ||
      !/^[a-f0-9]{64}$/.test(confirmation.digest || "") ||
      (!confirmation.amount && (!confirmation.before || !confirmation.after))
    )
      throw new ApiError("denied", "INVALID_PREPARED_ACTION");
    if(['gifting.collection.share','gifting.collection.delete'].includes(prepared.id)&&!collectionReviewDetails(prepared))throw new ApiError('denied','INVALID_COLLECTION_REVIEW_SCOPE');
    return prepared;
  }
  async command(
    operation: Operation,
    command: Command<Record<string, unknown>>,
    instanceId?: string,
  ): Promise<Outcome<unknown>> {
    const body = JSON.stringify(envelope(operation, command)),commandEpoch=this.epoch;
    let upload:ScheduledMediaFile|undefined;
    if(operation==='gifting.schedule.media'){upload=instanceId?this.scheduledMediaFiles.get(instanceId):undefined;if(!upload||upload.used||upload.epoch!==this.epoch||upload.fingerprint!==this.scheduledMediaFingerprint(upload.action)||canonicalJSON(command.input)!==canonicalJSON(upload.action.input)||command.expectedVersion!==null||!upload.action.expiresAt||Date.parse(upload.action.expiresAt)<=Date.now()){if(upload)this.releaseScheduledGiftMedia(upload.action);throw new ApiError('stale','GIFT_MEDIA_FILE_SELECTION_REQUIRED')}upload.used=true;}
    let r: any;
    try {
      r = await this.request(OPERATIONS[operation].path, {
        method: "POST",
        body:upload?upload.file:body,
        ...(upload?{signal:upload.controller.signal}:{}),
        headers: {...(instanceId?{"X-Thistleglass-Action-Instance":instanceId}:{}),...(upload?{"Content-Type":upload.file.type,"X-Thistleglass-Command-Key":command.idempotencyKey,"X-Thistleglass-Expected-Version":"null"}:{})},
      });
    } catch (e) {
      if(upload&&((e as Error).name==='AbortError'||upload.epoch!==this.epoch))throw new ApiError('pending','OPERATION_OUTCOME_UNKNOWN',command.idempotencyKey,false);
      if (
        e instanceof ApiError &&
        (["NETWORK_UNAVAILABLE", "INVALID_RESPONSE"].includes(e.code) ||
          e.code.startsWith("HTTP_5") ||
          e.httpStatus > 0)
      )
        throw new ApiError(
          "pending",
          "OPERATION_OUTCOME_UNKNOWN",
          command.idempotencyKey,
          false,
        );
      throw e;
    }finally{if(upload)this.releaseScheduledGiftMedia(upload.action)}
    if (r.kind !== "ok" || !isVersion(r.version) || !Object.hasOwn(r, "value"))
      throw new ApiError(
        "pending",
        "UNCONFIRMED_RESULT",
        command.idempotencyKey,
        false,
      );
    if(isScheduledGiftAction({id:operation})){const valid=await isScheduledGiftWriteReceipt(operation,command.expectedVersion,command.input,r,upload?.principal,command.idempotencyKey);if(!valid)throw new ApiError('pending','UNCONFIRMED_RESULT',command.idempotencyKey,false);if(commandEpoch!==this.epoch)throw new ApiError('pending','OPERATION_OUTCOME_UNKNOWN',command.idempotencyKey,false);}
    if(operation === "identity.sellerSetup" && !isSellerSetupReceipt(command.input,r))
      throw new ApiError("pending","UNCONFIRMED_RESULT",command.idempotencyKey,false);
    return r;
  }
  private uploadReceipt(raw: any): UploadReceipt {
    if (raw?.kind === "ok" && !isVersion(raw.version))
      throw new ApiError("error", "INVALID_UPLOAD_RECEIPT");
    const value = raw?.kind === "ok" ? raw.value : raw;
    if (
      !value ||
      typeof value.id !== "string" ||
      !value.id ||
      !["ready", "quarantined", "rejected"].includes(value.state) ||
      !isVersion(value.version)
    )
      throw new ApiError("error", "INVALID_UPLOAD_RECEIPT");
    if (value.state === "ready") {
      if (
        !value.value ||
        typeof value.value !== "object" ||
        Array.isArray(value.value)
      )
        throw new ApiError("error", "INVALID_UPLOAD_VALUE");
      rejectAuthority(value.value);
    }
    return value;
  }
  async upload(
    file: File,
    resource: { id: string; version: number },
    purpose: NonNullable<Field["uploadPurpose"]>,
    idempotencyKey: string,
    maxBytes = 10 * 1024 * 1024,
    sourceInstanceId?: string,
  ): Promise<UploadReceipt> {
    if (
      !Number.isSafeInteger(maxBytes) ||
      maxBytes < 1 ||
      file.size > maxBytes ||
      file.size === 0
    )
      throw new ApiError("error", "UPLOAD_SIZE_INVALID");
    if (!resource?.id || !isVersion(resource.version))
      throw new ApiError("stale", "ACTION_RESOURCE_REQUIRED");
    const form = new FormData();
    form.set(
      "command",
      JSON.stringify({
        idempotencyKey,
        expectedVersion: resource.version,
        input: { resource, purpose },
      }),
    );
    form.set("file", file, file.name);
    let raw: unknown;
    try {
      raw = await this.request("/api/thistleglass/uploads", {
        method: "POST",
        ...((purpose === "listing" || purpose === "digital") && sourceInstanceId ? {headers:{"X-Thistleglass-Action-Instance":sourceInstanceId}} : {}),
        body: form,
      });
    } catch (e) {
      if (
        e instanceof ApiError &&
        ([
          "NETWORK_UNAVAILABLE",
          "INVALID_RESPONSE",
          "INVALID_UPLOAD_RECEIPT",
          "INVALID_UPLOAD_VALUE",
        ].includes(e.code) ||
          e.httpStatus > 0 ||
          e.code.startsWith("HTTP_5"))
      )
        throw new ApiError(
          "pending",
          "UPLOAD_OUTCOME_UNKNOWN",
          idempotencyKey,
          false,
        );
      throw e;
    }
    // Parsing is local: no local rejection proves the uploaded effect did not happen.
    try {
      return this.uploadReceipt(raw);
    } catch {
      throw new ApiError(
        "pending",
        "UPLOAD_OUTCOME_UNKNOWN",
        idempotencyKey,
        false,
      );
    }
  }
  async uploadStatus(id: string): Promise<UploadReceipt> {
    return this.uploadReceipt(
      await this.request("/api/thistleglass/uploads/" + encodeURIComponent(id)),
    );
  }
  async signIn(
    username: string,
    password: string,
  ): Promise<{ session: string; label: string; expiresAt: string }> {
    // Invalidate prior private responses before starting an identity transition.
    this.setSession("");
    const epoch = this.epoch;
    const r: any = await this.request("/api/thistleglass/session/local", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
    const v = r.kind === "ok" ? r.value : r;
    if (
      !v ||
      typeof v.session !== "string" ||
      !v.session ||
      typeof v.label !== "string" ||
      !Number.isFinite(Date.parse(v.expiresAt)) ||
      Date.parse(v.expiresAt) <= Date.now()
    )
      throw new ApiError("error", "INVALID_SESSION");
    if (epoch !== this.epoch) throw new ApiError("denied", "SESSION_CHANGED");
    this.setSession(v.session);
    return v;
  }
  async session(): Promise<{ label: string; expiresAt: string }> {
    const r: any = await this.request("/api/thistleglass/session");
    const v = r.kind === "ok" ? r.value : r;
    if (
      !v ||
      typeof v.label !== "string" ||
      !Number.isFinite(Date.parse(v.expiresAt))
    )
      throw new ApiError("denied", "INVALID_SESSION");
    return { label: v.label, expiresAt: v.expiresAt };
  }
  sellerAgents(): Promise<any> { return this.request("/api/thistleglass/seller/agents"); }
  createAgentKey(name: string): Promise<any> { return this.request("/api/thistleglass/seller/agents", { method: "POST", body: JSON.stringify({ name }) }); }
  revokeAgentKey(id: string): Promise<any> { return this.request("/api/thistleglass/seller/agents/revoke", { method: "POST", body: JSON.stringify({ id }) }); }
  decideAgentProposal(id: string, decision: "used" | "dismissed"): Promise<any> { return this.request("/api/thistleglass/seller/agents/proposals/decide", { method: "POST", body: JSON.stringify({ id, decision }) }); }
  async agentProposalImage(id: string, index: number): Promise<Blob> {
    const r = await this.fetcher(new URL("/api/thistleglass/seller/agents/proposals/image?id=" + encodeURIComponent(id) + "&index=" + index, this.origin), { credentials: "omit", cache: "no-store", headers: this.token ? { Authorization: `Bearer ${this.token}` } : {} });
    if (!r.ok) throw new ApiError("error", "IMAGE_UNAVAILABLE");
    return r.blob();
  }
  async downloadMyData(): Promise<Blob> {
    const r = await this.fetcher(new URL("/api/thistleglass/account/data-export", this.origin), { credentials: "omit", cache: "no-store", headers: this.token ? { Authorization: `Bearer ${this.token}` } : {} });
    if (!r.ok) { let code = "HTTP_" + r.status; try { code = (await r.json()).code ?? code; } catch {} throw new ApiError(r.status === 403 ? "denied" : "error", code); }
    return r.blob();
  }
  closeAccount(): Promise<any> { return this.request("/api/thistleglass/account/close", { method: "POST", body: JSON.stringify({ confirm: "CLOSE" }) }); }
  deletionQueue(): Promise<any> { return this.request("/api/thistleglass/ops/deletion-requests"); }
  /** Shop shipping price per order. */
  shopShipping(): Promise<any> { return this.request("/api/thistleglass/seller/shipping"); }
  saveShopShipping(amount: string): Promise<any> { return this.request("/api/thistleglass/seller/shipping", { method: "POST", body: JSON.stringify({ amount }) }); }
  /** Seller payouts through Whop. */
  sellerPayouts(): Promise<any> { return this.request("/api/thistleglass/seller/payouts"); }
  linkSellerPayouts(email: string): Promise<any> { return this.request("/api/thistleglass/seller/payouts", { method: "POST", body: JSON.stringify({ email }) }); }
  /** Buyer payment on Whop for a confirmed checkout. */
  whopCheckout(freezeId: string): Promise<any> { return this.request("/api/thistleglass/checkout/whop?freeze=" + encodeURIComponent(freezeId)); }
  startWhopCheckout(freezeId: string): Promise<any> { return this.request("/api/thistleglass/checkout/whop", { method: "POST", body: JSON.stringify({ freezeId }) }); }
  /** Manual seller review. */
  sellerReview(): Promise<any> { return this.request("/api/thistleglass/seller/review"); }
  saveSellerReview(details: Record<string, unknown>): Promise<any> { return this.request("/api/thistleglass/seller/review", { method: "POST", body: JSON.stringify(details) }); }
  uploadReviewFile(file: File, kind: string): Promise<any> { return this.request("/api/thistleglass/seller/review/file", { method: "POST", body: file, headers: { "Content-Type": file.type || "application/octet-stream", "x-file-kind": kind, "x-file-name": encodeURIComponent(file.name.slice(0, 120)) } }); }
  deleteReviewFile(id: string): Promise<any> { return this.request("/api/thistleglass/seller/review/file?id=" + encodeURIComponent(id), { method: "DELETE" }); }
  sellerReviewQueue(): Promise<any> { return this.request("/api/thistleglass/ops/seller-reviews"); }
  decideSellerReview(shop: string, action: "approve" | "reject" | "needs_info", note?: string): Promise<any> { return this.request("/api/thistleglass/ops/seller-reviews", { method: "POST", body: JSON.stringify({ shop, action, note }) }); }
  async sellerReviewFile(id: string): Promise<Blob> {
    const r = await this.fetcher(new URL("/api/thistleglass/ops/seller-reviews?file=" + encodeURIComponent(id), this.origin), { credentials: "omit", cache: "no-store", headers: this.token ? { Authorization: `Bearer ${this.token}` } : {} });
    if (!r.ok) { let code = "HTTP_" + r.status, message = ""; try { const j = await r.json(); code = j.code ?? code; message = j.message ?? ""; } catch {} const e = new ApiError("error", code) as ApiError & { detail?: string }; e.detail = message; throw e; }
    return r.blob();
  }
  decideDeletion(account: string, action: "complete" | "hold" | "resume"): Promise<any> { return this.request("/api/thistleglass/ops/deletion-requests", { method: "POST", body: JSON.stringify({ account, action }) }); }
  async signOut() {
    // Capture the old bearer for revocation but clear it locally even on network loss.
    const token = this.token;
    this.setSession("");
    await this.request("/api/thistleglass/session", {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
  }
  async commandStatus(key: string): Promise<CommandStatus> {
    const r = await this.request(
      "/api/thistleglass/commands/" + encodeURIComponent(key),
    );
    return parseCommandStatus(r, key);
  }
  async unresolved(
    cursor?: string,
  ): Promise<{ items: CommandStatus[]; next?: string }> {
    const r: any = await this.request(
      "/api/thistleglass/commands?state=unresolved" +
        (cursor ? "&cursor=" + encodeURIComponent(cursor) : ""),
    );
    const v = r.kind === "ok" ? r.value : undefined;
    if (
      !isVersion(r.version) ||
      !v ||
      !Array.isArray(v.items) ||
      v.items.length > 100 ||
      (v.next !== undefined && typeof v.next !== "string")
    )
      throw new ApiError("pending", "COMMAND_STATUS_UNRESOLVED");
    return {
      items: v.items.map((item: unknown) => parseCommandStatus(item)),
      next: v.next,
    };
  }
  /** Current authenticated in-app gift projection; private native fields and
   * arbitrary media URLs are excluded by the closed consumer codec. */
  async scheduledGift(id:string,signal?:AbortSignal):Promise<ScheduledGiftProjection>{
    if(!/^[a-f0-9]{64}$/.test(id))throw new ApiError("denied","INVALID_GIFT_LOCATOR");
    return parseScheduledGiftProjection(await this.request("/api/thistleglass/gifting/scheduled/current?id="+id,{signal,redirect:"error"}));
  }
  /** Native media elements cannot attach the original memory Bearer token.
   * Read the bounded, current-authorized same-API bytes into a local Blob;
   * the owner component controls/revokes its local object URL. */
  async scheduledGiftMedia(id:string,version:number,signal?:AbortSignal):Promise<Blob>{
    if(!/^[a-f0-9]{64}$/.test(id))throw new ApiError("denied","INVALID_GIFT_LOCATOR");
    if(!isVersion(version))throw new ApiError("stale","INVALID_GIFT_VERSION");
    const epoch=this.epoch;let response:Response;
    try{response=await this.fetcher(new URL("/api/thistleglass/gifting/scheduled/media?id="+id,this.origin),{method:"GET",credentials:"omit",cache:"no-store",redirect:"error",signal,headers:{Accept:"audio/ogg,audio/mpeg,video/mp4,video/webm",...(this.token?{Authorization:`Bearer ${this.token}`}:{})}})}
    catch(error){if((error as Error).name==="AbortError")throw error;throw new ApiError("error","GIFT_MEDIA_UNAVAILABLE")}
    const cancel=async()=>{try{await response.body?.cancel()}catch{}};
    if(epoch!==this.epoch){await cancel();throw new ApiError("denied","SESSION_CHANGED")}
    if(response.status===401)this.revokeCurrentSession(epoch);
    if(!response.ok||response.status!==200||response.redirected){await cancel();throw new ApiError(response.status===401||response.status===403?"denied":response.status===503?"pending":"error",response.status===503?"GIFT_SOURCE_SETUP_REQUIRED":"GIFT_MEDIA_UNAVAILABLE")}
    if(response.headers.get("X-Thistleglass-Scheduled-Version")!==String(version)){await cancel();throw new ApiError("stale","GIFT_MATERIAL_CHANGED")}
    const mime=response.headers.get("Content-Type"),length=response.headers.get("Content-Length")??"";
    // Original private transport ceiling; the native current technical supplier
    // imposes its own equal or lower bound before accepting or yielding bytes.
    if(!mime||!["audio/ogg","audio/mpeg","video/mp4","video/webm"].includes(mime)||response.headers.get("X-Content-Type-Options")?.toLowerCase()!=="nosniff"||!/^([1-9][0-9]*)$/.test(length)||!Number.isSafeInteger(Number(length))||Number(length)>10*1024*1024||!response.body){await cancel();throw new ApiError("denied","INVALID_GIFT_MEDIA")}
    const reader=response.body.getReader(),parts:ArrayBuffer[]=[];let size=0;
    try{
      while(true){const chunk=await reader.read();if(epoch!==this.epoch)throw new ApiError("denied","SESSION_CHANGED");if(signal?.aborted)throw new DOMException("Aborted","AbortError");if(chunk.done)break;size+=chunk.value.byteLength;if(size>Number(length)||size>10*1024*1024)throw new ApiError("denied","INVALID_GIFT_MEDIA");parts.push(chunk.value.slice().buffer as ArrayBuffer)}
      if(size!==Number(length))throw new ApiError("denied","INVALID_GIFT_MEDIA");return new Blob(parts,{type:mime});
    }catch(error){try{await reader.cancel()}catch{};throw error}finally{reader.releaseLock()}
  }
  async download(
    id: string,
    kind: "digital" | "attachment" = "digital",
  ): Promise<{ blob: Blob; filename: string }> {
    if (kind !== "digital" && kind !== "attachment")
      throw new ApiError("denied", "INVALID_DOWNLOAD");
    const epoch = this.epoch;
    let r: Response;
    try {
      r = await this.fetcher(
        new URL(
          kind === "attachment"
            ? "/api/thistleglass/attachments/" +
              encodeURIComponent(id) +
              "/download"
            : "/api/thistleglass/digital/downloads/" + encodeURIComponent(id),
          this.origin,
        ),
        {
          method: "POST",
          credentials: "omit",
          cache: "no-store",
          headers: {
            Accept: "application/octet-stream",
            ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
          },
        },
      );
    } catch {
      throw new ApiError("error", "DOWNLOAD_UNAVAILABLE", "", true);
    }
    if (epoch !== this.epoch) throw new ApiError("denied", "SESSION_CHANGED");
    if (r.status === 401) this.revokeCurrentSession(epoch);
    if (!r.ok)
      throw new ApiError(
        r.status === 401 || r.status === 403
          ? "denied"
          : r.status === 409 || r.status === 410
            ? "stale"
            : "error",
        "DOWNLOAD_UNAVAILABLE",
      );
    const disposition = r.headers.get("Content-Disposition") || "";
    if (
      !/^attachment(?:;|$)/i.test(disposition) ||
      r.headers.get("X-Content-Type-Options")?.toLowerCase() !== "nosniff" ||
      /json|html/i.test(r.headers.get("Content-Type") || "")
    )
      throw new ApiError("denied", "INVALID_DOWNLOAD");
    const blob = await r.blob();
    if (epoch !== this.epoch) throw new ApiError("denied", "SESSION_CHANGED");
    const name = /filename="([^"\r\n]+)"/i.exec(disposition)?.[1] || "download";
    return {
      blob,
      filename: name.replace(/[\\/\x00-\x1f\x7f]/g, "_").slice(0, 200),
    };
  }
}

export type StatusOperation = Operation | "upload.prepare";
export type CommandStatus = {
  state: "committed" | "pending" | "unknown";
  operation: StatusOperation;
  idempotencyKey: string;
  commandDigest: string;
  resource: { id: string; version: number };
  instanceId?: string;
  outcome?: Outcome<unknown>;
  lookupVersion: number;
};
export function parseCommandStatus(raw: any, key?: string): CommandStatus {
  const v = raw?.kind === "ok" ? raw.value : undefined;
  if (
    !isVersion(raw?.version) ||
    !v ||
    !["committed", "pending", "unknown"].includes(v.state) ||
    !(
      Object.hasOwn(OPERATIONS, v.operation) || v.operation === "upload.prepare"
    ) ||
    typeof v.idempotencyKey !== "string" ||
    !v.idempotencyKey ||
    (key !== undefined && key !== v.idempotencyKey) ||
    !/^[a-f0-9]{64}$/.test(v.commandDigest) ||
    !v.resource ||
    typeof v.resource.id !== "string" ||
    !isVersion(v.resource.version) ||
    (v.instanceId !== undefined && typeof v.instanceId !== "string")
  )
    throw new ApiError("pending", "COMMAND_STATUS_UNRESOLVED", key || "");
  if (v.state === "committed") {
    if (
      !v.outcome ||
      !["ok", "denied", "conflict"].includes(v.outcome.kind) ||
      (v.outcome.kind === "ok"
        ? !isVersion(v.outcome.version) || !Object.hasOwn(v.outcome, "value")
        : typeof v.outcome.code !== "string" ||
          typeof v.outcome.referenceId !== "string" ||
          typeof v.outcome.retryable !== "boolean")
    )
      throw new ApiError("pending", "COMMAND_STATUS_UNRESOLVED", key || "");
  } else if (v.outcome !== undefined)
    throw new ApiError("pending", "COMMAND_STATUS_UNRESOLVED", key || "");
  if(v.operation === "identity.sellerSetup" && v.state === "committed" && v.outcome.kind === "ok" &&
    (v.resource.version!==1 || !isSellerSetupReceipt({kind:v.outcome.value?.kind,setupFor:{id:v.resource.id,version:1}},v.outcome)))
    throw new ApiError("pending","COMMAND_STATUS_UNRESOLVED",key||"");
  if(isScheduledGiftAction({id:v.operation})&&v.state==='committed'&&v.outcome.kind==='ok'&&!isScheduledGiftStatusReceipt(v.operation,v.resource,v.outcome))throw new ApiError('pending','COMMAND_STATUS_UNRESOLVED',key||v.idempotencyKey);
  return { ...v, lookupVersion: raw.version };
}

/** Exact deterministic browser digest; never serialize authority or lossy numbers. */
export function canonicalJSON(value: unknown): string {
  function encode(v: unknown): string {
    if (v === null || typeof v === "string" || typeof v === "boolean")
      return JSON.stringify(v);
    if (typeof v === "number" && Number.isSafeInteger(v))
      return JSON.stringify(v);
    if (Array.isArray(v)) return "[" + Array.from(v, encode).join(",") + "]";
    if (
      v &&
      typeof v === "object" &&
      (Object.getPrototypeOf(v) === Object.prototype ||
        Object.getPrototypeOf(v) === null)
    ) {
      rejectAuthority(v);
      if (
        Object.hasOwn(v, "minor") &&
        (typeof (v as any).minor !== "string" ||
          !/^(?:0|-?[1-9][0-9]*)$/.test((v as any).minor))
      )
        throw new ApiError("error", "INVALID_MONEY");
      return (
        "{" +
        Object.keys(v)
          .sort()
          .map(
            (k) =>
              JSON.stringify(k) +
              ":" +
              encode((v as Record<string, unknown>)[k]),
          )
          .join(",") +
        "}"
      );
    }
    throw new ApiError("error", "INVALID_COMMAND_VALUE");
  }
  return encode(value);
}
export async function commandDigest(
  operation: StatusOperation,
  expectedVersion: number | null,
  input: Record<string, unknown>,
): Promise<string> {
  const bytes = new TextEncoder().encode(
    canonicalJSON({ operation, expectedVersion, input }),
  );
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (b) =>
    b.toString(16).padStart(2, "0"),
  ).join("");
}

export function validateField(field: Field, value: unknown): boolean {
  if (field.type === "checkbox")
    return typeof value === "boolean" && (!field.mustAccept || value === true);
  if (field.required && (value === "" || value === undefined || value === null))
    return false;
  if (value === "" && !field.required) return true;
  if (field.type === "file")
    return (
      !!value && typeof value === "object" && !(value as any).uploadPending
    );
  if (field.type === "number")
    return (
      /^-?\d+$/.test(String(value)) &&
      Number.isSafeInteger(Number(value)) &&
      (field.min === undefined || Number(value) >= field.min) &&
      (field.max === undefined || Number(value) <= field.max)
    );
  if (typeof value !== "string" || value.length > 20000) return false;
  if (field.type === "email") return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  if (field.type === "select") return !!field.options?.includes(value);
  return true;
}
